'use client';

/* eslint-disable no-restricted-imports */
import { useRouter } from 'next/navigation';
import { useNavigationProgressContextSafe } from '@/contexts/NavigationProgressContext';

interface NavigateOptions {
  scroll?: boolean;
}

enum PrefetchKind {
  AUTO = 'auto',
  FULL = 'full',
  TEMPORARY = 'temporary',
}
interface PrefetchOptions {
  kind: PrefetchKind;
}

type AppRouterInstance = {
  /**
   * Navigate to the previous history entry.
   */
  back(): void;
  /**
   * Navigate to the next history entry.
   */
  forward(): void;
  /**
   * Refresh the current page.
   */
  refresh(): void;
  /**
   * Navigate to the provided href.
   * Pushes a new history entry.
   */
  push(href: string, options?: NavigateOptions): void;
  /**
   * Navigate to the provided href.
   * Replaces the current history entry.
   */
  replace(href: string, options?: NavigateOptions): void;
  /**
   * Prefetch the provided href.
   */
  prefetch(href: string, options?: PrefetchOptions): void;
};

/**
 * Checks if the URL is an internal navigation (same origin, not a hash-only change).
 */
const isInternalNavigation = (href: string): boolean => {
  try {
    const url = new URL(href, window.location.origin);
    const currentUrl = new URL(window.location.href);

    // External link (different origin)
    if (url.origin !== currentUrl.origin) return false;

    // Same URL - no navigation needed
    if (url.href === currentUrl.href) return false;

    // Hash-only change on same page
    if (url.pathname === currentUrl.pathname && url.search === currentUrl.search && url.hash !== currentUrl.hash) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
};

/**
 * https://github.com/vercel/next.js/issues/65335#issuecomment-2583401824
 * `useSafeRouter` is a drop‑in replacement for `useRouter` that applies the
 * same navigation guard used by `<SafeLink />`.
 *
 * This hook also triggers the page transition loading indicator when navigating.
 *
 * ```tsx
 * const router = useSafeRouter();
 * router.push('/about-us/');  // internal route - uses Next.js router with loading indicator
 * router.push('/notice/');    // external route - uses window.location with properly prefixed URL
 * ```
 */
export const useSafeRouter = (): AppRouterInstance => {
  const router = useRouter();
  const navigationProgress = useNavigationProgressContextSafe();

  const startNavigationIfInternal = (href: string): void => {
    if (navigationProgress && isInternalNavigation(href)) {
      navigationProgress.startNavigation();
    }
  };

  return {
    ...router,
    /**
     * Navigate to the provided href with loading indicator.
     * @param href https://github.com/vercel/next.js/issues/65335#issuecomment-2583401824
     */
    push: (href, options) => {
      startNavigationIfInternal(href);
      router.push(href, options);
    },
    /**
     * Navigate to the provided href (replace) with loading indicator.
     * https://github.com/vercel/next.js/issues/65335#issuecomment-2583401824
     * @param href
     */
    replace: (href, options) => {
      startNavigationIfInternal(href);
      router.replace(href, options);
    },
    /**
     * Navigate to the previous history entry with loading indicator.
     */
    back: () => {
      navigationProgress?.startNavigation();
      router.back();
    },
    /**
     * Navigate to the next history entry with loading indicator.
     */
    forward: () => {
      navigationProgress?.startNavigation();
      router.forward();
    },
  } satisfies AppRouterInstance;
};
